import Image from 'next/image';
import { useTheme } from 'styled-components';

import * as S from './styles';
import React from 'react';

export enum ButtonStyle {
    BASE = 'BASE',
    BLACK = 'BLACK',
    RED = 'RED',
    DELETE = 'DELETE',
    WARNING = 'WARNING',
    WHITE = 'WHITE',
    OUTLINEGREEN = 'OUTLINEGREEN',
    OUTLINEWHITE = 'OUTLINEWHITE',
    LINK = 'LINK',
}

export enum ButtonSize {
    XXSMALL = 'XXSMALL',
    XSMALL = 'XSMALL',
    SMALL = 'SMALL',
    LARGE = 'LARGE',
}

export enum LoaderType {
    TEAL = 'TEAL',
    WHITE = 'WHITE',
}

interface ButtonProps {
    text: string;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
    loading?: boolean;
    buttonType?: ButtonStyle;
    size?: ButtonSize;
    icon?: React.ReactNode;
    loader?: LoaderType;
    setIconRight?: boolean | null;
    loadingHeight?: string | null;
    loadingWidth?: string | null;
    className?: string;
}

export const Button: React.FC<ButtonProps> = ({
    text,
    onClick,
    disabled,
    loading,
    buttonType,
    size,
    icon,
    loader,
    setIconRight,
    loadingHeight,
    loadingWidth,
    className,
}: ButtonProps) => {
    let component: React.ReactNode;

    const theme = useTheme();

    const ButtonStyles = {
        [ButtonStyle.BASE]: {
            color: theme.accent,
            hoverColor: '#007261',
            hoverTextColor: theme.greyLight,
            textColor: theme.greyLight,
            skipBold: false,
            fontSize: '1rem',
            borderColor: undefined,
        },
        [ButtonStyle.BLACK]: {
            color: theme.primary,
            hoverColor: theme.greyDark,
            hoverTextColor: theme.greyLight,
            textColor: theme.greyLight,
            skipBold: false,
            fontSize: '1rem',
            borderColor: undefined,
        },
        [ButtonStyle.RED]: {
            color: '#A80000',
            hoverColor: '#e34f4f',
            hoverTextColor: theme.greyLight,
            textColor: theme.greyLight,
            skipBold: false,
            fontSize: '1rem',
            borderColor: undefined,
        },
        [ButtonStyle.DELETE]: {
            color: theme.deleteBase,
            hoverColor: '#ffd3d4',
            hoverTextColor: theme.deleteText,
            textColor: theme.deleteText,
            skipBold: true,
            fontSize: '0.85rem',
            borderColor: undefined,
        },
        [ButtonStyle.WARNING]: {
            color: theme.warningBase,
            hoverColor: '#fff2b2',
            hoverTextColor: theme.lightLabel,
            textColor: theme.lightLabel,
            skipBold: true,
            fontSize: '0.85rem',
            borderColor: undefined,
        },
        [ButtonStyle.WHITE]: {
            color: '#fff',
            hoverTextColor: theme.primary,
            textColor: theme.primary,
            hoverColor: '#E2E2E2',
            skipBold: true,
            borderColor: '#E2E2E2',
            fontSize: '1rem',
        },
        [ButtonStyle.OUTLINEGREEN]: {
            color: 'inherit',
            hoverColor: '#007261',
            hoverTextColor: theme.greyLight,
            textColor: theme.accent,
            skipBold: true,
            borderColor: theme.accent,
            fontSize: '1rem',
        },
        [ButtonStyle.OUTLINEWHITE]: {
            color: 'inherit',
            hoverColor: theme.greyLight,
            hoverTextColor: theme.primary,
            textColor: theme.lightLabel,
            skipBold: true,
            borderColor: theme.lightBorder,
            fontSize: '1rem',
        },
        [ButtonStyle.LINK]: {
            color: 'inherit',
            hoverColor: '#f0f2f5',
            hoverTextColor: theme.primary,
            textColor: '#6b7280',
            skipBold: true,
            borderColor: undefined,
            fontSize: '1rem',
        },
    };

    const buttonColor = buttonType
        ? ButtonStyles[buttonType]
        : ButtonStyles[ButtonStyle.BASE];

    const ButtonSizes = {
        [ButtonSize.XXSMALL]: {
            height: '1.6rem',
        },
        [ButtonSize.XSMALL]: {
            height: '2rem',
        },
        [ButtonSize.SMALL]: {
            height: '2.4rem',
        },
        [ButtonSize.LARGE]: {
            height: '2.875rem',
        },
    };

    const LoaderTypes = {
        [LoaderType.WHITE]: {
            src: '/static/images/loader-wh.svg',
        },
        [LoaderType.TEAL]: {
            src: '/static/images/loader-teal.svg',
        },
    };

    const buttonSize = size ? ButtonSizes[size] : ButtonSizes[ButtonSize.LARGE];
    const loaderType = loader
        ? LoaderTypes[loader]
        : LoaderTypes[LoaderType.WHITE];

    if (loading) {
        component = (
            <S.Img $loadingHeight={loadingHeight} $loadingWidth={loadingWidth}>
                <Image
                    src={loaderType.src}
                    alt='Laddar'
                    className='rotate'
                    fill
                    sizes={'100vw'}
                    style={{
                        objectFit: 'cover',
                    }}
                />
            </S.Img>
        );
    } else if (icon) {
        component = (
            <S.Icon $setIconRight={setIconRight}>
                {setIconRight ? (
                    <>
                        <S.IconText>{text}</S.IconText>
                        {icon}
                    </>
                ) : (
                    <>
                        {icon}
                        <S.IconText>{text}</S.IconText>
                    </>
                )}
            </S.Icon>
        );
    } else {
        component = text;
    }

    return (
        <S.Button
            onClick={(e) => {
                onClick(e);
                e.currentTarget.blur();
            }}
            disabled={disabled}
            $color={buttonColor.color}
            $hoverColor={buttonColor.hoverColor}
            $hoverTextColor={buttonColor.hoverTextColor}
            $textColor={buttonColor.textColor}
            $buttonHeight={buttonSize.height}
            $skipBold={buttonColor.skipBold}
            $fontSize={buttonColor.fontSize}
            $borderColor={buttonColor.borderColor}
            className={className}
        >
            {component}
        </S.Button>
    );
};
